import { ChangeEvent, memo, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';

import { SmsCountry } from './helper';

interface Props {
  isLoading?: boolean;
  onPhoneNumberChange: { (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void };
  onSmsCountryChange: {
    (e: ChangeEvent<HTMLInputElement>): void;
  };
  phoneNumber: string;
  phoneNumberErrorMessage: string;
  smsCountry: string;
}

const SmsPhoneFragment = (props: Props): React.JSX.Element => {
  const { formatMessage } = useIntl();

  const {
    isLoading,
    onPhoneNumberChange,
    onSmsCountryChange,
    phoneNumber,
    phoneNumberErrorMessage,
    smsCountry,
  } = props;

  const SecondaryText = useCallback(
    (chunk) => (
      <Box display="inline" ml={1}>
        <Typography display="inline" color="textSecondary" variant="body2">
          {chunk}
        </Typography>
      </Box>
    ),
    []
  );

  return (
    <>
      <Box mt={2} mb={3}>
        <Typography gutterBottom>
          <FormattedMessage defaultMessage="To enable SMS two-step authentication please enter your phone number. We'll send you an SMS code to complete your setup." />
        </Typography>
      </Box>
      <Box my={3}>
        <Grid container spacing={2}>
          <Grid item sm={12} md={5}>
            <FormControl variant="outlined" fullWidth>
              <InputLabel id="sms-country-label">
                <FormattedMessage defaultMessage="Select Country" />
              </InputLabel>

              <Select
                variant="standard"
                labelId="sms-country-label"
                id="sms-country"
                value={smsCountry}
                onChange={onSmsCountryChange}
                label={formatMessage({ defaultMessage: 'Select Country' })}
                disabled={isLoading}
              >
                <MenuItem value={SmsCountry.CA}>
                  <FormattedMessage
                    defaultMessage="Canada <SecondaryText>(+1)</SecondaryText>"
                    values={{
                      SecondaryText,
                    }}
                  />
                </MenuItem>

                <MenuItem value={SmsCountry.US}>
                  <FormattedMessage
                    defaultMessage="United States <SecondaryText>(+1)</SecondaryText>"
                    values={{
                      SecondaryText,
                    }}
                  />
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item sm={12} md={7}>
            <FormControl variant="outlined" fullWidth>
              <TextField
                id="phoneNumber"
                value={phoneNumber}
                onChange={onPhoneNumberChange}
                label={formatMessage({ defaultMessage: 'Enter Phone Number' })}
                autoFocus
                autoComplete="off"
                disabled={isLoading}
                error={Boolean(phoneNumberErrorMessage)}
                helperText={phoneNumberErrorMessage}
                required
              />
            </FormControl>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default memo(SmsPhoneFragment);
